/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { formatTemplateData, getTemplatesList } from 'api/services/AssetsService';
import {
	deleteMediaService,
	generateFreeMediaService,
	generateMediaService,
	getFaceReplaceCost,
	getMediaFeedService,
	removeImageBackground,
	submitMediaFeedback,
} from 'api/services/MediaService.ts';
import {
	getListControlNetModels,
	getLoras,
	getModelCategories,
	getModels,
	getRandomPrompt,
	getStyleCategories,
	getStyles,
} from 'api/services/PlaygroundService.ts';
import { toBase64 } from 'api/services/StudioService';
import { controlNetModelsConfig } from 'core/config/controlNetModelsConfig';
import { featureConfig } from 'core/config/featureConfig';
import { samplersConfig } from 'core/config/samplersConfig';

import { AmplitudeEvents } from 'core/constants/AmplitudeEvents';
import {
	DEFAULT_NEGATIVE_PROMPT_VALUE,
	DEFAULT_PROMPT_VALUE,
	DEFAULT_SELECTED_FLUX_MODEL,
	DEFAULT_SELECTED_FLUX_STYLE,
	DEFAULT_SELECTED_MODEL,
	DEFAULT_SELECTED_STYLE,
	PERSIST_PENDING_GENERATIONS_TIME_LIMIT,
} from 'core/constants/constants';
import { ApiStage } from 'core/constants/enums/ApiStages.ts';
import { AspectRatio } from 'core/constants/enums/AspectRatioEnum.ts';
import { Qualities } from 'core/constants/enums/AvailableQualitiesEnum.ts';
import { defaultSelectedData } from 'core/constants/enums/CharacterPrompt.ts';
import { ControlNetModelsType } from 'core/constants/enums/ControlNetModelsType.ts';
import { FeedViews } from 'core/constants/enums/FeedViews.ts';
import { GenerationModelTypes } from 'core/constants/enums/GenerationModelTypes.ts';
import { GenerationStyleTypes } from 'core/constants/enums/GenerationStyleTypes.ts';
import { GenerationType } from 'core/constants/enums/GenerationType.ts';
import { RendernetErrors } from 'core/constants/enums/RendernetErrors.ts';
import { ReusedComponentIDs } from 'core/constants/enums/ReusedComponentIDs.ts';
import { Samplers } from 'core/constants/enums/SamplersEnum.ts';
import { StudioLeftSidebarPopoverViews } from 'core/constants/enums/StudioLeftSidebarPopoverViews.ts';
import { StudioMediaTypeEnums } from 'core/constants/enums/StudioMediaTypeEnums.ts';
import { StudioSidebarViews } from 'core/constants/enums/StudioSidebarViews.ts';
import { TemplateType } from 'core/constants/enums/TemplateType.ts';
import { ToastType } from 'core/constants/enums/ToastTypeEnum.ts';
import { UpscaleResolution } from 'core/constants/enums/UpscaleResolution.ts';
import { LocalStorageKeys } from 'core/constants/LocalStorageKeys';
import { PostHogEvents } from 'core/constants/PostHogEvents';
import { sendToast } from 'core/layouts/components/toast/ToastUtils';
import { TEXT_TO_IMAGE } from 'core/navigation/Routes';
import { getMusicVideoAsync } from 'core/store/reducers/intermediateMediaReducer/IntermediateMediaSlice';
import { captureAmplitudeEvent } from 'core/utils/AmplitudeUtils.ts';
import { LoggerUtil } from 'core/utils/loggerUtil.ts';
import { capturePosthogEvent } from 'core/utils/PosthogUtil.ts';
import {
	handleSomethingWentWrongErrorWithToast,
	noNullCheckForObjectValues,
	scrollToTop,
	toggleModelSelection,
} from 'core/utils/utils';

const userPreferences = JSON.parse(localStorage.getItem(LocalStorageKeys.USER_PREFERENCES));

const promptDefaultInput = DEFAULT_PROMPT_VALUE;
const negativePromptDefaultInput = DEFAULT_NEGATIVE_PROMPT_VALUE;
const samplerDefaultInput = samplersConfig[Samplers.DPM_2M_KARRAS].name;
const samplingStepsDefaultInput = 25;
const APGuidanceDefaultInput = 7;
export const batchSizeDefaultInput = 1;
const seedDefaultInput = '-1';
const randomiseSeedDefaultInput = false;
const aspectRatioDefaultInput = AspectRatio.PORTRAIT;
const qualityDefaultInput =
	userPreferences?.quality &&
	userPreferences.quality !== Qualities.PRO &&
	userPreferences.quality !== Qualities.ULTRA
		? userPreferences.quality
		: Qualities.PLUS;

const defaultSelectedModel = [DEFAULT_SELECTED_MODEL];
const defaultStyle = DEFAULT_SELECTED_STYLE;
const defaultSelectedFluxModels = DEFAULT_SELECTED_FLUX_MODEL;
const defaultSelectedFluxStyles = DEFAULT_SELECTED_FLUX_STYLE;
const whatToChangeDefaultInput = '';
const changeToDefaultInput = '';

const trueTouchDefaultImage = null;
const trueTouchDefaultScaleFactor = 2;
const trueTouchDefaultRealismEffect = 0.2;
export const defaultOutputStudioMedia =
	'https://storage.googleapis.com/render-net.appspot.com/studio-skeleton.png';

const controlNetDefaultEnabled = false;
const controlNetDefaultImage = null;
const controlNetNonSDXLDefaultType = controlNetModelsConfig[ControlNetModelsType.SD15];
const controlNetSDXLDefaultType = controlNetModelsConfig[ControlNetModelsType.SDXL];
const controlNetFluxDefaultType = controlNetModelsConfig[ControlNetModelsType.Flux];

const controlNetControlDefaultMode = 0;
const controlNetResizeDefaultMode = 1;

const lipSyncDefaultVideoAssetData = null;
const lipSyncDefaultImageAssetData = null;
const lipSyncDefaultAudioAssetData = null;
const lipSyncDefaultVoiceData = null;
const lipSyncDefaultScript = '';
const upscaleDefaultImage = null;
const upscaleDefaultOutputResolution = UpscaleResolution.RESOLUTION_4K;

const videoAnyoneDefaultImage = null;
const videoAnyoneDefaultPrompt = '';
const lipSyncDefaultIsFreeVideoUsed = false;

const videoFaceSwapDefaultImage = null;
const faceSwapDefaultVideoAssetData = null;
const faceSwapDefaultRenderingPreference = null;

const intensityDefaultCount = 7;

const initialState = {
	randomisePrompt: true,
	studioAppBarMediaType: StudioMediaTypeEnums.IMAGE,
	sidebarGenerationTab: StudioLeftSidebarPopoverViews.MAGIC,
	sidebarView: null,
	feedView: userPreferences?.feedView ? userPreferences.feedView : FeedViews.MEDIUM,
	feedType: userPreferences?.feedType ? userPreferences.feedType : FeedViews.LIST,

	models: [],
	fluxModels: [],
	lightningModels: [],
	getModelsAPIStage: ApiStage.LOADING,
	selectedModels: defaultSelectedModel,
	selectedFluxModels: defaultSelectedFluxModels,
	selectedLightningModels: [],

	modelCategories: [],
	getModelCategoriesAPIStage: ApiStage.IDLE,

	defaultSelected: defaultSelectedData,
	characterPromptTextarea: '',

	getStudioFeedAPIStage: ApiStage.IDLE,

	studioFeedBatchWiseMap: {},
	studioFeedChronologicalBatchIdList: [],
	studioMediaMap: {},
	studioMediaList: [],

	studioFeedTotalPages: 1,
	studioFeedCurrentPage: 1,

	promptInput: promptDefaultInput,
	homePagePromptInput: '',
	negativePromptInput: negativePromptDefaultInput,
	samplerInput: samplerDefaultInput,
	samplingStepsInput: samplingStepsDefaultInput,
	AIGuidanceInput: APGuidanceDefaultInput,
	batchSizeInput: batchSizeDefaultInput,
	seedInput: seedDefaultInput,
	randomiseSeedInput: randomiseSeedDefaultInput,
	aspectRatioInput: userPreferences?.aspectRatioInput
		? userPreferences.aspectRatioInput
		: aspectRatioDefaultInput,

	quality: qualityDefaultInput,

	changeAnythingImage: null,
	whatToChange: whatToChangeDefaultInput,
	changeTo: changeToDefaultInput,

	trueTouchImage: trueTouchDefaultImage,
	isTrueTouchImageUploading: false,
	trueTouchScaleFactor: trueTouchDefaultScaleFactor,
	trueTouchRealismEffect: trueTouchDefaultRealismEffect,

	loras: [],
	getLorasAPIStage: ApiStage.IDLE,
	selectedLoras: [],

	controlNetEnabled: controlNetDefaultEnabled,
	controlNetImage: controlNetDefaultImage,
	controlNetModelsStudio: null,
	controlNetNonSDXLType: controlNetNonSDXLDefaultType,
	controlNetSDXLType: controlNetSDXLDefaultType,
	controlNetFluxType: controlNetFluxDefaultType,

	controlNetControlMode: controlNetControlDefaultMode,
	controlNetResizeMode: controlNetResizeDefaultMode,
	studioControlNetModelsApiStage: ApiStage.IDLE,

	facelockEnabled: false,
	characterLabEnabled: false,
	facelockImage: null,

	currentGeneration: null,
	currentChangeItGeneration: null,
	currentTrueTouchGeneration: null,
	currentUpscaleGeneration: null,
	currentVideoFaceSwapGeneration: null,
	currentVideoAnyoneGeneration: null,
	currentFaceReplaceGeneration: null,
	currentMusicVideoGeneration: null,
	currentClothingChangeGeneration: null,
	currentProductPhotoGeneration: null,

	generateStudioMediaAPIStage: ApiStage.IDLE,
	outputStudioMedia: defaultOutputStudioMedia,

	openTooManyGenerationsModal: false,

	openDeletePromptConfirmationPopUp: false,
	openMultipleDeletePromptConfirmationPopUp: false,
	deletePromptDetails: [],
	multiDeletePromptDetails: [],
	deletePromptAPIStage: ApiStage.IDLE,

	randomisePromptAPIStage: ApiStage.IDLE,

	styleCategories: [],
	getStyleCategoriesApiStage: ApiStage.IDLE,

	styles: [],
	fluxStyles: [],
	getStylesApiStage: ApiStage.IDLE,
	selectedStyles: defaultStyle,
	selectedFluxStyles: defaultSelectedFluxStyles,

	lipSyncVideoAssetData: lipSyncDefaultVideoAssetData,
	lipSyncImageAssetData: lipSyncDefaultImageAssetData,
	lipSyncAudioAssetData: lipSyncDefaultAudioAssetData,
	lipSyncVoiceData: lipSyncDefaultVoiceData,
	lipSyncScript: lipSyncDefaultScript,

	upscaleInputImage: upscaleDefaultImage,
	upscaleOutputResolution: upscaleDefaultOutputResolution,

	videoAnyoneInputImage: videoAnyoneDefaultImage,
	videoAnyonePrompt: videoAnyoneDefaultPrompt,
	videoAnyoneDuration: null,
	videoAnyoneEndFrameImage: { id: null, url: null },
	videoAnyoneModelName: null,
	videoAnyoneModelProvider: null,
	videoAnyoneInputImageId: '',
	lipSyncIsFreeVideoUsed: lipSyncDefaultIsFreeVideoUsed,
	selectedImages: [],

	isStudioSidebarOpen: false,
	studioMediaFeedbackModalOpen: false,
	submitStudioMediaFeedbackApiStage: ApiStage.IDLE,

	promptDetailModalOpen: false,

	faceReplaceInputImage: null,
	faceReplaceInputImageId: '',
	faceReplaceTargetImages: [],
	faceReplaceTargetImageIndex: -1,
	faceReplaceCostAPIStage: ApiStage.IDLE,
	faceReplaceCost: 0,

	faceSwapImageAssetData: videoFaceSwapDefaultImage,
	faceSwapVideoAssetData: faceSwapDefaultVideoAssetData,
	faceSwapRenderingPreference: faceSwapDefaultRenderingPreference,

	musicVideoAudioAssetData: null,
	musicVideoTheme: '',
	musicVideoCharacter: null,
	musicVideoAspectRatio: AspectRatio.WIDE,
	musicVideoTemplatesData: null,
	musicVideoTemplatesApiStage: ApiStage.IDLE,

	// Clothing Change
	clothingInputImage: { id: null, url: null },
	clothingTargetImage: { id: null, url: null },
	clothingTypeValue: null,
	studioContextMenu: { position: null, mediaData: null },
	showGuide: false,

	// Product Photo
	productPhotoImage: { id: null, url: null, displayOrder: null },
	productPhotoWithoutBackgroundImageApiStage: ApiStage.IDLE,
	productPhotoWithoutBackgroundImage: { id: null, url: null },
	productPhotoBackgroundImage: { id: null, url: null, displayOrder: null },
	productPhotoObjectLocationImage: { id: null, url: null, status: 'idle' },
	productPhotoCompositeImage: { id: null, url: null, status: 'idle' },
	productPhotoMode: 'steady',
	productPhotoReflection: false,
	productPhotoObjectTransformValues: { rotation: null, flipX: false, flipY: false },
	productPhotoImageToVideoGuideToggle: { modalOpen: false, doNotShowValue: false },

	// Image Reference
	imageReferenceStyle: { id: null, url: null },
	intensityCountInput: intensityDefaultCount,
};

export const studioInitialState = initialState;

// Models Service
export const getModelsAsync = createAsyncThunk('studio/getModels', async (type, { dispatch }) =>
	getModels(type, { dispatch }),
);
export const getModelCategoriesAsync = createAsyncThunk(
	'studio/getModelCategories',
	async (_, { dispatch }) => getModelCategories({ dispatch }),
);
export const getControlNetModelsAsync = createAsyncThunk(
	'studio/getControlNetModels',
	async (_, { dispatch }) => getListControlNetModels({ dispatch }),
);

// Styles Service
export const getStylesAsync = createAsyncThunk('studio/getStyles', async (type, { dispatch }) =>
	getStyles(type, { dispatch }),
);
export const getStylesCategoryAsync = createAsyncThunk(
	'studio/getStylesCategory',
	async (_, { dispatch }) => getStyleCategories({ dispatch }),
);

// Loras Service
export const getLorasAsync = createAsyncThunk('studio/getLoras', async (_, { dispatch }) =>
	getLoras({ dispatch }),
);

// Cost service
export const getStudioFaceReplaceCostAsync = createAsyncThunk(
	'studio/getFaceReplaceCost',
	async (_, { dispatch }) => getFaceReplaceCost({ dispatch }),
);

export const getStudioFeedAsync = createAsyncThunk('studio/getStudioFeedAsync', async (newPage) =>
	getMediaFeedService({
		newPage,
		playground: 'studio',
	}),
);

export const getAudioAssetsTemplatesAsync = createAsyncThunk(
	'studio/getAudioAssetsTemplates',
	async (_, { dispatch }) => {
		return getTemplatesList(TemplateType.MUSIC_VIDEO_SONGS, { dispatch });
	},
);

export const uploadStudioControlNetImageFileAsync = createAsyncThunk(
	'studio/uploadControlNetImageFile',
	async (file) => toBase64(file),
);
export const uploadStudioControlNetImageLinkAsync = createAsyncThunk(
	'studio/uploadControlNetImageLink',
	async (link) => Promise.resolve(link),
);

export const uploadStudioFaceLockImageFileAsync = createAsyncThunk(
	'studio/uploadFaceLockImageFile',
	async (file) => toBase64(file),
);
export const uploadStudioFaceLockImageLinkAsync = createAsyncThunk(
	'studio/uploadFaceLockImageLink',
	async (link) => Promise.resolve(link),
);
export const uploadStudioChangeAnythingImageFileAsync = createAsyncThunk(
	'studio/uploadChangeAnythingImageFile',
	async (file) => toBase64(file),
);

export const uploadStudioChangeAnythingImageLinkAsync = createAsyncThunk(
	'studio/uploadChangeAnythingImageLink',
	async (link) => Promise.resolve(link),
);
export const uploadStudioTrueTouchImageFileAsync = createAsyncThunk(
	'studio/uploadTrueTouchImageFile',
	async (file) => toBase64(file),
);
export const uploadStudioTrueTouchImageLinkAsync = createAsyncThunk(
	'studio/uploadTrueTouchImageLink',
	async (link) => Promise.resolve(link),
);
export const runGenerateStudioMediaAsync = createAsyncThunk(
	'studio/runGenerateStudioMedia',
	async (data, { getState }) => {
		const { generateParams } = data;
		const {
			models,
			fluxModels,
			fluxStyles,
			lightningModels,
			styles,
			promptInput: prompt,
			sidebarGenerationTab,
			imageReferenceStyle,
		} = getState().studio;
		const isFluxGeneration =
			sidebarGenerationTab === StudioSidebarViews.FLUX && !featureConfig.isFluxControlNetEnabled;
		if (generateParams.isFreeGeneration) {
			LoggerUtil.logInfo('Generating free media on /text_to_image with params:', {
				...generateParams,
			});
			return generateFreeMediaService({
				...generateParams,
			});
		}
		const finalGenerateParams = {
			...generateParams,
			models: { ...models, ...fluxModels, ...lightningModels },
			styles: [...styles, ...fluxStyles],
			prompt: generateParams.prompt ?? prompt,
			controlNetOptions: isFluxGeneration ? null : generateParams.controlNetOptions,
			imageReferenceOptions:
				imageReferenceStyle.url != null ? generateParams.imageReferenceOptions : null,
		};
		LoggerUtil.logInfo('Generating media on /text_to_image with params:', finalGenerateParams);
		return generateMediaService(finalGenerateParams);
	},
);

export const multiDeleteImageAsync = createAsyncThunk(
	'app/multiDeleteImage',
	async (data, { getState }) => {
		// eslint-disable-next-line no-use-before-define
		const deletePrompt = multiDeletePromptDetailsSelector(getState());
		return deleteMediaService(deletePrompt.map((media) => media.generationId));
	},
);

export const randomiseStudioPromptAsync = createAsyncThunk(
	'studio/randomisePromptAsync',
	async () => getRandomPrompt(),
);

export const submitStudioMediaFeedbackAsync = createAsyncThunk(
	'canvas/submitFeed',
	async (data, { dispatch }) =>
		submitMediaFeedback(data.mediaId, data.comment, data.rating, { dispatch }),
);

export const productPhotoStudioRemoveImageBackgroundAsync = createAsyncThunk(
	'studio/productPhotoRemoveBackgroundImage',
	async (inputImageId, { dispatch }) => removeImageBackground(inputImageId, { dispatch }),
);

export const studioSlice = createSlice({
	name: 'studio',
	initialState,
	reducers: {
		setRandomiseStudioPrompt: (state, action) => {
			state.randomisePrompt = action.payload;
		},

		setStudioSidebarView: (state, action) => {
			const selectedSidebarView = action.payload;
			state.sidebarView = selectedSidebarView;
			if (
				selectedSidebarView === StudioSidebarViews.FLUX ||
				selectedSidebarView === StudioSidebarViews.MAGICMODE ||
				selectedSidebarView === StudioSidebarViews.PROMODE
			) {
				state.sidebarGenerationTab = selectedSidebarView;
			}
		},
		setStudioAppBarMediaType: (state, action) => {
			state.studioAppBarMediaType = action.payload;
		},
		setStudioGenerationSidebarView: (state, action) => {
			state.sidebarGenerationTab = action.payload;
		},

		setFeedView: (state, action) => {
			state.feedView = action.payload;
			localStorage.setItem(
				LocalStorageKeys.USER_PREFERENCES,
				JSON.stringify({
					aspectRatioInput: state.aspectRatioInput,
					quality: state.quality,
					feedView: action.payload,
					feedType: state.feedType,
				}),
			);
		},

		setFeedType: (state, action) => {
			state.feedType = action.payload;
			localStorage.setItem(
				LocalStorageKeys.USER_PREFERENCES,
				JSON.stringify({
					aspectRatioInput: state.aspectRatioInput,
					quality: state.quality,
					feedView: state.feedView,
					feedType: action.payload,
				}),
			);
		},

		setStudioSettings: (state, action) => {
			const {
				settings,
				fromFeed = false,
				fromHomePromptsModal = false,
				fromProfile = false,
				fromHomePage = false,
				setControlNet = true,
				setControlNetImage = true,
				setFacelock = true,
				setFacelockImage = true,
				fromLandingPage = false,
			} = action.payload;

			state.samplerInput = settings?.sampler ?? samplerDefaultInput;
			state.samplingStepsInput = settings?.steps ?? samplingStepsDefaultInput;
			state.AIGuidanceInput = settings?.guidance ?? APGuidanceDefaultInput;

			if (fromHomePromptsModal || fromProfile || fromFeed || fromHomePage || fromLandingPage) {
				state.promptInput = settings?.prompt ?? promptDefaultInput;
				state.negativePromptInput = settings?.negativePrompt ?? negativePromptDefaultInput;

				state.batchSizeInput = 1;

				state.seedInput = settings?.seed ?? seedDefaultInput;
				state.randomiseSeedInput = randomiseSeedDefaultInput;

				state.aspectRatioInput = settings?.aspectRatioType ?? aspectRatioDefaultInput;

				state.quality = settings?.quality ?? qualityDefaultInput;

				state.outputStudioMedia = settings?.media ?? defaultOutputStudioMedia;
				if (settings?.media) {
					state.outputStudioMedia = [
						...(Array.isArray(settings.media) ? settings.media : [settings.media]),
					];
				}
				if (settings?.changeIt) {
					state.whatToChange = settings?.changeIt?.whatToChange ?? '';
					state.changeTo = settings?.changeIt?.changeTo ?? '';
				}
			}

			const resetControlNetSettingsToDefault = () => {
				state.controlNetEnabled = controlNetDefaultEnabled;
				state.controlNetNonSDXLType = controlNetNonSDXLDefaultType;
				state.controlNetSDXLType = controlNetSDXLDefaultType;
				state.controlNetFluxType = controlNetFluxDefaultType;
				state.controlNetControlMode = controlNetControlDefaultMode;
				state.controlNetResizeMode = controlNetResizeDefaultMode;
			};
			const resetFaceLockSettingsToDefault = () => {
				state.facelockEnabled = false;
			};

			if (settings?.attributes) {
				const { controlNet, facelock } = settings.attributes;
				if (controlNet) {
					const enableControlNet = noNullCheckForObjectValues(controlNet);
					state.controlNetEnabled = enableControlNet;

					if (enableControlNet) {
						if (setControlNetImage && !fromLandingPage) {
							state.controlNetImage = controlNet?.inputImage;
						}

						state.controlNetControlMode = controlNet?.controlMode
							? controlNet?.controlMode
							: controlNetControlDefaultMode;
						state.controlNetResizeMode = controlNet?.resizeMode
							? controlNet?.resizeMode
							: controlNetResizeDefaultMode;

						state.controlNetNonSDXLType =
							controlNet?.nonsdxl && state.controlNetModelsStudio
								? Object.values(state.controlNetModelsStudio).find(
										(config) => config.id === controlNet?.nonsdxl?.id,
									)
								: controlNetNonSDXLDefaultType;
						state.controlNetSDXLType =
							controlNet?.sdxl && state.controlNetModelsStudio
								? Object.values(state.controlNetModelsStudio).find(
										(config) => config.id === controlNet?.sdxl?.id,
									)
								: controlNetSDXLDefaultType;

						// TODO: FLUX ControlNet Changes
						state.controlNetFluxType =
							controlNet?.flux && state.controlNetModelsStudio
								? Object.values(state.controlNetModelsStudio).find(
										(config) => config.id === controlNet?.flux?.id,
									)
								: controlNetFluxDefaultType;
					} else {
						resetControlNetSettingsToDefault();
					}
				} else {
					resetControlNetSettingsToDefault();
				}
				if (facelock) {
					if (setFacelockImage && !fromLandingPage) {
						state.facelockImage = facelock?.inputImage;
					}
					state.facelockEnabled = true;
				} else {
					resetFaceLockSettingsToDefault();
				}
			} else if (setControlNet || setFacelock) {
				resetControlNetSettingsToDefault();
				resetFaceLockSettingsToDefault();
			}
			if (fromHomePage || fromLandingPage) {
				state.quality = settings?.quality ?? Qualities.PLUS;
			}

			if (fromProfile || fromFeed) {
				state.seedInput = settings?.seed ?? seedDefaultInput;
				state.randomiseSeedInput = randomiseSeedDefaultInput;
			}
			if (fromHomePromptsModal) {
				state.seedInput = seedDefaultInput;
				state.randomiseSeedInput = true;
			}
		},

		setStudioSelectedModels: (state, action) => {
			const { model, replace = false, fromLandingPage = false } = action.payload;
			let selectedModelsCopy = [...state.selectedModels];

			if (fromLandingPage) {
				const models = [];
				model.forEach((m) => {
					models.push(m.model);
				});
				selectedModelsCopy = models;
			} else if (replace) {
				if (Array.isArray(model)) {
					selectedModelsCopy = model;
				} else {
					selectedModelsCopy = [model];
				}
			} else if (selectedModelsCopy.includes(model)) {
				selectedModelsCopy = selectedModelsCopy.filter((modelName) => modelName !== model);
			} else {
				selectedModelsCopy.push(model);
			}

			state.selectedModels = selectedModelsCopy;
		},

		setStudioSelectedFluxModels: (state, action) => {
			state.selectedFluxModels = toggleModelSelection(
				[...state.selectedFluxModels],
				action.payload,
			);
		},

		setStudioSelectedLightningModels: (state, action) => {
			state.selectedLightningModels = toggleModelSelection(
				[...state.selectedLightningModels],
				action.payload,
			);
		},

		setStudioSelectedStyles: (state, action) => {
			const { styles, style, replace = false } = action.payload;
			let selectedStylesCopy = [...state.selectedStyles];

			// overwrite entire list of models on replace = true
			// otherwise add/remove single model passed as param
			if (replace) {
				selectedStylesCopy = styles;
			} else if (selectedStylesCopy.includes(style)) {
				selectedStylesCopy = selectedStylesCopy.filter((styleId) => styleId !== style);
			} else {
				selectedStylesCopy.push(style);
			}

			state.selectedStyles = selectedStylesCopy;
		},
		setStudioSelectedFluxStyles: (state, action) => {
			state.selectedFluxStyles = toggleModelSelection(
				[...state.selectedFluxStyles],
				action.payload,
			);
		},
		setStudioPromptInput: (state, action) => {
			state.promptInput = action.payload;
		},
		setHomePagePromptInput: (state, action) => {
			state.homePagePromptInput = action.payload;
		},
		setStudioNegativePromptInput: (state, action) => {
			state.negativePromptInput = action.payload;
		},
		setStudioSamplerInput: (state, action) => {
			state.samplerInput = action.payload;
		},
		setStudioSamplingStepsInput: (state, action) => {
			state.samplingStepsInput = action.payload;
		},
		setStudioAIGuidanceInput: (state, action) => {
			state.AIGuidanceInput = action.payload;
		},
		setStudioBatchSizeInput: (state, action) => {
			state.batchSizeInput = action.payload;
		},
		setStudioSeedInput: (state, action) => {
			state.seedInput = action.payload;
		},
		setStudioRandomiseSeedInput: (state, action) => {
			state.randomiseSeedInput = action.payload;
		},
		setStudioAspectRatioInput: (state, action) => {
			state.aspectRatioInput = action.payload;
			localStorage.setItem(
				LocalStorageKeys.USER_PREFERENCES,
				JSON.stringify({
					aspectRatioInput: action.payload,
					quality: state.quality,
					feedView: state.feedView,
					feedType: state.feedType,
				}),
			);
		},
		setStudioQuality: (state, action) => {
			state.quality = action.payload;
			localStorage.setItem(
				LocalStorageKeys.USER_PREFERENCES,
				JSON.stringify({
					aspectRatioInput: state.aspectRatioInput,
					quality: action.payload,
					feedView: state.feedView,
					feedType: state.feedType,
				}),
			);
		},
		setOutputStudioMedia: (state, action) => {
			state.outputStudioMedia = action.payload;
		},

		setStudioCurrentGeneration: (state, action) => {
			state.currentGeneration = action.payload;
		},

		setStudioCurrentChangeItGeneration: (state, action) => {
			state.currentChangeItGeneration = action.payload;
		},

		setStudioCurrentTrueTouchGeneration: (state, action) => {
			state.currentTrueTouchGeneration = action.payload;
		},
		setStudioProductPhotoGeneration: (state, action) => {
			state.currentProductPhotoGeneration = action.payload;
		},

		setStudioCurrentUpscaleGeneration: (state, action) => {
			state.currentUpscaleGeneration = action.payload;
		},
		setStudioCurrentVideoFaceSwapGeneration: (state, action) => {
			state.currentVideoFaceSwapGeneration = action.payload;
		},
		setStudioCurrentVideoAnyoneGeneration: (state, action) => {
			state.currentVideoAnyoneGeneration = action.payload;
		},
		setStudioFaceReplaceGeneration: (state, action) => {
			state.currentFaceReplaceGeneration = action.payload;
		},
		setStudioMusicVideoGeneration: (state, action) => {
			state.currentMusicVideoGeneration = action.payload;
		},
		setStudioClothingChangeGeneration: (state, action) => {
			state.currentClothingChangeGeneration = action.payload;
		},

		resetStudioCurrentGenerations: (state) => {
			state.currentGeneration = null;
			state.currentChangeItGeneration = null;
			state.currentTrueTouchGeneration = null;
			state.currentUpscaleGeneration = null;
			state.currentVideoAnyoneGeneration = null;
			state.currentFaceReplaceGeneration = null;
			state.currentVideoFaceSwapGeneration = null;
			state.currentMusicVideoGeneration = null;
			state.currentClothingChangeGeneration = null;
			state.currentProductPhotoGeneration = null;
		},

		setCharacterPromptInputs: (state, action) => {
			const { key, value } = action.payload;
			state.defaultSelected[key] = value;
		},
		setRandomizeCharacterPrompt: (state, action) => {
			state.defaultSelected = action.payload;
		},

		toggleTooManyGenerationsModal: (state, action) => {
			state.openTooManyGenerationsModal = action.payload;
		},

		setStudioSelectedLoras: (state, action) => {
			state.selectedLoras = action.payload;
		},

		setStudioFeed: (state, action) => {
			const { batchIdList = [], batchMap = {} } = action.payload;
			state.studioFeedBatchWiseMap = batchMap;
			state.studioFeedChronologicalBatchIdList = batchIdList;
		},
		setGetStudioFeedAPIStage: (state, action) => {
			state.getStudioFeedAPIStage = action.payload;
		},
		setStudioFeedCurrentPage: (state, action) => {
			state.studioFeedCurrentPage = action.payload;
		},

		setStudioControlNetEnabled: (state, action) => {
			state.controlNetEnabled = action.payload;
		},
		setStudioControlNetImage: (state, action) => {
			state.controlNetImage = action.payload;
		},
		setStudioControlNetNonSDXLType: (state, action) => {
			state.controlNetNonSDXLType = action.payload;
		},
		setStudioControlNetSDXLType: (state, action) => {
			state.controlNetSDXLType = action.payload;
		},
		setStudioControlNetFluxType: (state, action) => {
			state.controlNetFluxType = action.payload;
		},
		resetStudioControlNetImage: (state) => {
			state.controlNetImage = null;
			state.controlNetEnabled = false;
		},
		setStudioControlNetControlMode: (state, action) => {
			state.controlNetControlMode = action.payload;
		},
		setStudioControlNetResizeMode: (state, action) => {
			state.controlNetResizeMode = action.payload;
		},
		setStudioCharacterLabEnabled: (state, action) => {
			state.characterLabEnabled = action.payload;
		},
		setStudioFacelockEnabled: (state, action) => {
			state.facelockEnabled = action.payload;
		},
		setStudioFacelockImage: (state, action) => {
			state.facelockImage = action.payload;
		},
		resetStudioFacelockImage: (state) => {
			state.facelockImage = null;
			state.facelockEnabled = false;
		},
		setStudioChangeAnythingImage: (state, action) => {
			state.changeAnythingImage = action.payload;
		},
		resetStudioChangeAnythingImage: (state) => {
			state.changeAnythingImage = null;
		},
		setWhatToChange: (state, action) => {
			state.whatToChange = action.payload;
		},
		setChangeTo: (state, action) => {
			state.changeTo = action.payload;
		},

		// TrueTouch Image and settings
		setStudioTrueTouchImage: (state, action) => {
			state.trueTouchImage = action.payload;
		},
		resetStudioTrueTouchImage: (state) => {
			state.trueTouchImage = null;
		},
		setStudioTrueTouchScaleFactor: (state, action) => {
			state.trueTouchScaleFactor = action.payload;
		},
		setStudioTrueTouchRealismEffect: (state, action) => {
			state.trueTouchRealismEffect = action.payload;
		},

		// Face swap image and settings
		setStudioFaceSwapImageAssetData: (state, action) => {
			state.faceSwapImageAssetData = action.payload;
			state.faceSwapStudioImageAdded = true;
		},
		resetStudioFaceSwapImageAssetData: (state) => {
			state.faceSwapImageAssetData = null;
			state.faceSwapStudioImageAdded = false;
		},
		setStudioFaceSwapImageAdded: (state, action) => {
			state.faceSwapStudioImageAdded = action.payload;
		},

		setStudioFaceSwapVideoAdded: (state, action) => {
			state.faceSwapStudioVideoAdded = action.payload;
		},

		// Face swap video and settings
		setStudioFaceSwapVideoAssetData: (state, action) => {
			if (action.payload?.id) {
				state.faceSwapVideoAssetData = action.payload;
				state.faceSwapStudioVideoAdded = true;
			}
		},
		resetStudioFaceSwapVideoAssetData: (state) => {
			state.faceSwapVideoAssetData = null;
			state.faceSwapStudioVideoAdded = false;
			state.faceSwapRenderingPreference = null;
		},
		setStudioFaceSwapRenderingPreference: (state, action) => {
			state.faceSwapRenderingPreference = action.payload;
		},
		setFaceSwapFrames: (state, action) => {
			state.faceSwapFrames = action.payload;
		},

		// 8k-Upscale Image and settings
		setStudioUpscaleImage: (state, action) => {
			state.upscaleInputImage = action.payload;
		},
		resetStudioUpscaleImage: (state) => {
			state.upscaleInputImage = null;
		},

		setStudioVideoAnyonePrompt: (state, action) => {
			state.videoAnyonePrompt = action.payload;
		},
		setStudioVideoAnyoneDuration: (state, action) => {
			state.videoAnyoneDuration = action.payload;
		},
		setStudioVideoAnyoneImage: (state, action) => {
			state.videoAnyoneInputImage = action.payload;
		},
		setStudioVideoAnyoneImageId: (state, action) => {
			state.videoAnyoneInputImageId = action.payload;
		},
		resetStudioVideoAnyoneImage: (state) => {
			state.videoAnyoneInputImage = null;
			state.videoAnyoneInputImageId = '';
		},

		setStudioFaceReplaceImage: (state, action) => {
			state.faceReplaceInputImage = action.payload;
		},
		setStudioFaceReplaceImageId: (state, action) => {
			state.faceReplaceInputImageId = action.payload;
		},
		resetStudioFaceReplaceImage: (state) => {
			state.faceReplaceInputImage = null;
			state.faceReplaceInputImageId = '';
		},

		setStudioFaceReplaceTargetImages: (state, action) => {
			const { index } = action.payload;
			const targetImages = [...state.faceReplaceTargetImages];
			const existingIndex = targetImages.findIndex((img) => img.index === index);

			if (existingIndex !== -1) {
				targetImages[existingIndex] = action.payload;
			} else {
				targetImages.push(action.payload);
			}

			state.faceReplaceTargetImages = targetImages;
		},
		removeStudioFaceReplaceTargetImage: (state, action) => {
			const index = action.payload;
			state.faceReplaceTargetImages = state.faceReplaceTargetImages.filter(
				(img) => img.index !== index,
			);
		},
		resetStudioFaceReplaceTargetImages: (state) => {
			state.faceReplaceTargetImages = [];
		},

		setStudioFaceReplaceTargetImageIndex: (state, action) => {
			state.faceReplaceTargetImageIndex = action.payload;
		},

		setStudioUpscaleOutputResolution: (state, action) => {
			state.upscaleOutputResolution = action.payload;
		},

		setStudioLipSyncVideoAssetData: (state, action) => {
			state.lipSyncVideoAssetData = action.payload;
		},
		resetStudioLipSyncVideoAssetData: (state) => {
			state.lipSyncVideoAssetData = lipSyncDefaultVideoAssetData;
		},
		setStudioLipSyncImageAssetData: (state, action) => {
			state.lipSyncImageAssetData = action.payload;
		},
		resetStudioLipSyncImageAssetData: (state) => {
			state.lipSyncImageAssetData = lipSyncDefaultImageAssetData;
		},
		setStudioLipSyncAudioAssetData: (state, action) => {
			state.lipSyncAudioAssetData = action.payload;
		},
		resetStudioLipSyncAudioAssetData: (state) => {
			state.lipSyncAudioAssetData = lipSyncDefaultVideoAssetData;
		},
		setStudioLipSyncScript: (state, action) => {
			state.lipSyncScript = action.payload;
		},
		setStudioLipSyncVoiceData: (state, action) => {
			state.lipSyncVoiceData = action.payload;
		},
		setStudioIsLipSyncFreeVideoUsed: (state, action) => {
			state.lipSyncIsFreeVideoUsed = action.payload;
		},
		toggleMultiDeletePromptConfirmationModal: (state, action) => {
			const { open, prompt = null, title = null, subtitle = null } = action.payload;

			state.openMultipleDeletePromptConfirmationPopUp = { open, title, subtitle };
			state.multiDeletePromptDetails = prompt;
		},
		setStudioSelectedImages: (state, action) => {
			state.selectedImages = action.payload;
		},
		updateStudioSelectedImages: (state, action) => {
			const imageData = action.payload;
			const { generationId } = imageData;
			const { selectedImages } = state;
			const isSelected = selectedImages.some((image) => image.generationId === generationId);
			let updatedSelectedImages;
			if (isSelected) {
				updatedSelectedImages = selectedImages.filter(
					(image) => image.generationId !== generationId,
				);
			} else {
				updatedSelectedImages = [...selectedImages, imageData];
			}
			state.selectedImages = updatedSelectedImages;
		},
		toggleIsStudioSidebarOpen: (state, action) => {
			state.isStudioSidebarOpen = action.payload;
		},
		toggleStudioMediaFeedbackModal: (state, action) => {
			state.studioMediaFeedbackModalOpen = action.payload;
		},
		togglePromptDetailModalOpen: (state, action) => {
			state.promptDetailModalOpen = action.payload;
		},
		setStudioMusicVideoAudioAssetData: (state, action) => {
			state.musicVideoAudioAssetData = action.payload;
		},
		resetStudioMusicVideoAudioAssetData: (state) => {
			state.musicVideoAudioAssetData = null;
		},
		setStudioMusicVideoTheme: (state, action) => {
			state.musicVideoTheme = action.payload;
		},
		setStudioMusicVideoCharacter: (state, action) => {
			state.musicVideoCharacter = action.payload;
		},
		setStudioMusicVideoAspectRatio: (state, action) => {
			state.musicVideoAspectRatio = action.payload;
		},

		// change clothing
		setStudioClothingInputImage: (state, action) => {
			state.clothingInputImage = action.payload;
		},
		resetStudioClothingInputImage: (state) => {
			state.clothingInputImage = { id: null, url: null };
		},
		setStudioClothingTargetImage: (state, action) => {
			state.clothingTargetImage = action.payload;
		},
		resetStudioClothingTargetImage: (state) => {
			state.clothingTargetImage = { id: null, url: null };
		},
		setStudioClothingTypeValue: (state, action) => {
			state.clothingTypeValue = action.payload;
		},
		toggleStudioContextMenu: (state, action) => {
			state.studioContextMenu = action.payload;
		},
		toggleShowGuide: (state, action) => {
			const value = action.payload;
			if (value != null) {
				state.showGuide = value;
			} else state.showGuide = !state.showGuide;
		},
		resetStudioState: (state) => {
			Object.keys(initialState).forEach((key) => {
				state[key] = initialState[key];
			});
		},

		// Product Photo
		setStudioProductPhotoImage: (state, action) => {
			state.productPhotoImage = action.payload;
		},
		setStudioProductPhotoWithoutBackgroundImage: (state, action) => {
			state.productPhotoWithoutBackgroundImage = action.payload;
		},
		resetStudioProductPhotoImage: (state) => {
			state.productPhotoImage = { id: null, url: null, displayOrder: null };
			state.productPhotoWithoutBackgroundImage = { id: null, url: null };
		},
		setStudioProductPhotoBackgroundImage: (state, action) => {
			state.productPhotoBackgroundImage = action.payload;
		},
		setStudioProductPhotoObjectLocationImage: (state, action) => {
			state.productPhotoObjectLocationImage = action.payload;
		},
		setStudioProductPhotoCompositeImage: (state, action) => {
			state.productPhotoCompositeImage = action.payload;
		},
		resetStudioProductPhotoBackgroundImage: (state) => {
			state.productPhotoBackgroundImage = { id: null, url: null, displayOrder: null };
			state.productPhotoCompositeImage = { id: null, url: null, status: 'idle' };
			state.productPhotoObjectLocationImage = { id: null, url: null, status: 'idle' };
		},
		setStudioProductPhotoMode: (state, action) => {
			state.productPhotoMode = action.payload;
		},
		setStudioProductPhotoReflection: (state, action) => {
			state.productPhotoReflection = action.payload;
		},
		resetStudioProductPhotoInternalAssets: (state) => {
			state.productPhotoObjectLocationImage = { id: null, url: null, status: 'idle' };
			state.productPhotoCompositeImage = { id: null, url: null, status: 'idle' };
		},
		setStudioProductPhotoObjectTransformValues: (state, action) => {
			state.productPhotoObjectTransformValues = action.payload;
		},
		toggleStudioProductPhotoImageToVideoGuide: (state, action) => {
			const {
				modalOpen = state.productPhotoImageToVideoGuideToggle.modalOpen,
				doNotShowValue = state.productPhotoImageToVideoGuideToggle.doNotShowValue,
			} = action.payload;

			state.productPhotoImageToVideoGuideToggle = {
				modalOpen,
				doNotShowValue,
			};
		},
		setStudioModels: (state, action) => {
			state.models = action.payload;
		},
		setStudioStyles: (state, action) => {
			state.styles = action.payload;
		},
		setStudioFluxModels: (state, action) => {
			state.fluxModels = action.payload;
		},
		setStudioFluxStyles: (state, action) => {
			state.fluxStyles = action.payload;
		},

		setStudioImageReferenceStyle: (state, action) => {
			state.imageReferenceStyle = action.payload;
		},
		resetStudioImageReferenceStyle: (state) => {
			state.imageReferenceStyle = { id: null, url: null };
		},
		setStudioIntensityCountInput: (state, action) => {
			state.intensityCountInput = action.payload;
		},
		setStudioVideoAnyoneEndFrameImage: (state, action) => {
			state.videoAnyoneEndFrameImage = action.payload;
		},
		resetStudioVideoAnyoneEndFrameImage: (state) => {
			state.videoAnyoneEndFrameImage = { id: null, url: null };
		},
		setStudioVideoAnyoneModelName: (state, action) => {
			state.videoAnyoneModelName = action.payload;
		},
		setStudioVideoAnyoneModelProvider: (state, action) => {
			state.videoAnyoneModelProvider = action.payload;
		},
	},
	extraReducers: (builder) => {
		// get models
		builder.addCase(getModelsAsync.pending, (state) => {
			state.getModelsAPIStage = ApiStage.LOADING;
		});
		builder.addCase(getModelsAsync.fulfilled, (state, action) => {
			const { data, args } = action.payload;
			if (data.length < 1) state.getModelsAPIStage = ApiStage.ERROR;

			state.getModelsAPIStage = ApiStage.SUCCESS;
			if (args?.type === GenerationModelTypes.LIGHTNING) {
				state.lightningModels = data;
			} else if (args?.type === GenerationModelTypes.FLUX) {
				state.fluxModels = data;
			} else {
				state.models = data;
			}
		});
		builder.addCase(getModelsAsync.rejected, (state) => {
			state.getModelsAPIStage = ApiStage.ERROR;
			sendToast(ToastType.REFRESH_ON_API_FAILURE);
		});

		// get models categories
		builder.addCase(getModelCategoriesAsync.pending, (state) => {
			state.getModelCategoriesAPIStage = ApiStage.LOADING;
		});
		builder.addCase(getModelCategoriesAsync.fulfilled, (state, action) => {
			const { data } = action.payload;
			state.getModelCategoriesAPIStage = ApiStage.SUCCESS;
			if (data.length < 1) state.getModelCategoriesAPIStage = ApiStage.ERROR;
			state.modelCategories = data;
		});
		builder.addCase(getModelCategoriesAsync.rejected, (state, action) => {
			state.getModelCategoriesAPIStage = ApiStage.ERROR;
			const { error } = action;
			handleSomethingWentWrongErrorWithToast('API request failed', error);
		});

		// get loras
		builder.addCase(getLorasAsync.pending, (state) => {
			state.getLorasAPIStage = ApiStage.LOADING;
		});

		builder.addCase(getLorasAsync.fulfilled, (state, action) => {
			const { data } = action.payload;
			if (data.length < 1) state.getLorasAPIStage = ApiStage.ERROR;

			state.getLorasAPIStage = ApiStage.SUCCESS;
			state.loras = data;
		});

		builder.addCase(getLorasAsync.rejected, (state) => {
			state.getLorasAPIStage = ApiStage.ERROR;
			sendToast(ToastType.REFRESH_ON_API_FAILURE);
		});

		// get studio feed
		builder.addCase(getStudioFeedAsync.pending, (state) => {
			state.getStudioFeedAPIStage = ApiStage.LOADING;
		});
		builder.addCase(getStudioFeedAsync.fulfilled, (state, action) => {
			const {
				batchWiseHistoryMap,
				batchWiseHistoryChronologicalBatchIdList,
				pageSize,
				mediaList,
				mediaMap,
			} = action.payload;

			const {
				studioFeedBatchWiseMap: currentStudioFeedBatchWiseMap,
				studioFeedChronologicalBatchIdList: currentStudioFeedChronologicalBatchIdList,
				studioMediaList: currentStudioMediaList,
				studioMediaMap: currentStudioMediaMap,
			} = state;

			state.getStudioFeedAPIStage = ApiStage.SUCCESS;
			if (state.studioFeedCurrentPage === 1) {
				const pendingBatchWiseMap = {};
				const pendingMediaList = [];
				const pendingMediaMap = {};
				const pendingBatchIdList = currentStudioFeedChronologicalBatchIdList.filter((batchId) => {
					const batch = currentStudioFeedBatchWiseMap[batchId];
					const isPending = Object.keys(batch.derivedMedia).some(
						(mediaId) =>
							(batch.derivedMedia[mediaId]?.status === 'initiated' ||
								batch.derivedMedia[mediaId]?.status === 'pending' ||
								batch.derivedMedia[mediaId]?.loading) &&
							Math.ceil(Date.now() / 1000) - batch.date < PERSIST_PENDING_GENERATIONS_TIME_LIMIT,
					);
					if (isPending) {
						pendingBatchWiseMap[batchId] = batch;
						Object.keys(batch.derivedMedia).forEach((mediaId) => {
							pendingMediaList.push(mediaId);
							pendingMediaMap[mediaId] = currentStudioMediaMap[mediaId];
						});
					}
					return isPending;
				});

				state.studioFeedBatchWiseMap = {
					...pendingBatchWiseMap,
					...batchWiseHistoryMap,
				};
				state.studioFeedChronologicalBatchIdList = [
					...pendingBatchIdList,
					...batchWiseHistoryChronologicalBatchIdList.filter(
						(batchId) => !pendingBatchWiseMap[batchId],
					),
				];
				state.studioMediaMap = {
					...pendingMediaMap,
					...mediaMap,
				};
				state.studioMediaList = [
					...pendingMediaList,
					...mediaList.filter((mediaId) => !pendingMediaMap[mediaId]),
				];
			} else {
				state.studioFeedBatchWiseMap = {
					...currentStudioFeedBatchWiseMap,
					...batchWiseHistoryMap,
				};
				state.studioFeedChronologicalBatchIdList = [
					...currentStudioFeedChronologicalBatchIdList,
					...batchWiseHistoryChronologicalBatchIdList.filter(
						(batchId) => !currentStudioFeedBatchWiseMap[batchId],
					),
				];
				state.studioMediaMap = {
					...currentStudioMediaMap,
					...mediaMap,
				};
				state.studioMediaList = [
					...currentStudioMediaList,
					...mediaList.filter((mediaId) => !currentStudioMediaMap[mediaId]),
				];
			}
			// state.studioFeedTotalPages = calculatePageNumbers(listSize, pageSize);
			if (batchWiseHistoryChronologicalBatchIdList?.length >= pageSize) {
				state.studioFeedTotalPages += 1;
			} else if (!batchWiseHistoryChronologicalBatchIdList?.length) {
				state.studioFeedTotalPages -= 1;
			}
		});
		builder.addCase(getStudioFeedAsync.rejected, (state, action) => {
			const { error } = action;
			if (error?.code === RendernetErrors.INVALID_CREDENTIALS) {
				state.getStudioFeedAPIStage = ApiStage.IDLE;
			} else {
				state.getStudioFeedAPIStage = ApiStage.ERROR;
			}
			handleSomethingWentWrongErrorWithToast('API request failed', error);
		});
		builder.addCase(runGenerateStudioMediaAsync.pending, (state) => {
			if (state.seedInput === '') {
				state.seedInput = seedDefaultInput;
				state.randomiseSeedInput = true;
			}
			state.generateStudioMediaAPIStage = ApiStage.LOADING;
			state.outputStudioMedia = defaultOutputStudioMedia;

			scrollToTop(ReusedComponentIDs.STUDIO_FEED_SCROLL_IDHAR);
		});
		builder.addCase(runGenerateStudioMediaAsync.fulfilled, (state, action) => {
			const response = action.payload;
			const { batchId, batchDetails, mediaList, mediaMap } = response;

			state.generateStudioMediaAPIStage = ApiStage.SUCCESS;
			state.studioFeedBatchWiseMap[`${batchId}`] = batchDetails;
			state.studioFeedChronologicalBatchIdList = [
				`${batchId}`,
				...state.studioFeedChronologicalBatchIdList,
			];
			state.studioMediaList = [...mediaList, ...state.studioMediaList];
			state.studioMediaMap = {
				...mediaMap,
				...state.studioMediaMap,
			};
			if (
				batchDetails.generationType === GenerationType.OBJECT_PLACEMENT &&
				!state.productPhotoImageToVideoGuideToggle.doNotShowValue
			) {
				state.productPhotoImageToVideoGuideToggle = {
					...state.productPhotoImageToVideoGuideToggle,
					modalOpen: true,
				};
			}
		});
		builder.addCase(runGenerateStudioMediaAsync.rejected, (state, action) => {
			state.generateStudioMediaAPIStage = ApiStage.ERROR;
			const error = action.error.code;
			LoggerUtil.logError('[Generate Studio Media]: Error', action?.error);
			LoggerUtil.logInfo('[Generate Studio Media]: Parameters', action?.meta?.arg);

			if (error === RendernetErrors.NSFW_PROMPT) {
				captureAmplitudeEvent(AmplitudeEvents.NSFW_TOAST);
				capturePosthogEvent(PostHogEvents.NSFW_TOAST);
				if (action?.meta?.arg?.generateParams?.videoAnyoneOptions) {
					sendToast(ToastType.GEN_FAILED_DUE_TO_CONTENT_MODERATION);
				} else sendToast(ToastType.NSFW_CONTENT);
			} else if (error === RendernetErrors.TOO_MANY_PENDING_IMAGES) {
				state.openTooManyGenerationsModal = true;
			} else if (error === RendernetErrors.TOO_MANY_VIDEO_GENERATIONS) {
				state.openTooManyVideoGenerationsModal = true;
			} else if (error === RendernetErrors.TOO_MANY_LIPSYNC_GENERATIONS) {
				sendToast(ToastType.TOO_MANY_LIPSYNC_GENERATIONS);
			} else if (
				error !== RendernetErrors.INVALID_CREDENTIALS &&
				error !== RendernetErrors.BLOCKED_USER &&
				error !== RendernetErrors.INSUFFICIENT_CREDITS
			) {
				sendToast(ToastType.GENERATE_API_ERROR);
			}
		});

		// upload control net image file
		builder.addCase(uploadStudioControlNetImageFileAsync.fulfilled, (state, action) => {
			state.controlNetImage = action.payload;
			state.controlNetEnabled = true;
		});
		builder.addCase(uploadStudioControlNetImageFileAsync.rejected, () => {
			sendToast(ToastType.UPLOAD_IMAGE_ERROR);
		});
		// upload control net image through link
		builder.addCase(uploadStudioControlNetImageLinkAsync.fulfilled, (state, action) => {
			state.controlNetImage = action.payload;
			state.controlNetEnabled = true;
		});
		builder.addCase(uploadStudioControlNetImageLinkAsync.rejected, () => {
			sendToast(ToastType.UPLOAD_IMAGE_ERROR);
		});

		// upload facelock image file
		builder.addCase(uploadStudioFaceLockImageFileAsync.fulfilled, (state, action) => {
			state.facelockImage = action.payload;
			state.facelockEnabled = true;
		});
		builder.addCase(uploadStudioFaceLockImageFileAsync.rejected, () => {
			sendToast(ToastType.UPLOAD_IMAGE_ERROR);
		});
		// upload facelock image through link
		builder.addCase(uploadStudioFaceLockImageLinkAsync.fulfilled, (state, action) => {
			state.facelockImage = action.payload;
			state.facelockEnabled = true;
		});
		builder.addCase(uploadStudioFaceLockImageLinkAsync.rejected, () => {
			sendToast(ToastType.UPLOAD_IMAGE_ERROR);
		});

		// upload change anything image file
		builder.addCase(uploadStudioChangeAnythingImageFileAsync.fulfilled, (state, action) => {
			state.changeAnythingImage = action.payload;
		});
		builder.addCase(uploadStudioChangeAnythingImageFileAsync.rejected, () => {
			sendToast(ToastType.UPLOAD_IMAGE_ERROR);
		});
		// upload change anything image through link
		builder.addCase(uploadStudioChangeAnythingImageLinkAsync.fulfilled, (state, action) => {
			state.changeAnythingImage = action.payload;
		});
		builder.addCase(uploadStudioChangeAnythingImageLinkAsync.rejected, () => {
			sendToast(ToastType.UPLOAD_IMAGE_ERROR);
		});

		// upload true touch image file
		builder.addCase(uploadStudioTrueTouchImageFileAsync.pending, (state) => {
			state.isTrueTouchImageUploading = true;
		});
		builder.addCase(uploadStudioTrueTouchImageFileAsync.fulfilled, (state, action) => {
			state.isTrueTouchImageUploading = false;
			state.trueTouchImage = action.payload;
		});
		builder.addCase(uploadStudioTrueTouchImageFileAsync.rejected, (state) => {
			state.isTrueTouchImageUploading = false;
			sendToast(ToastType.UPLOAD_IMAGE_ERROR);
		});

		// upload true touch image through link
		builder.addCase(uploadStudioTrueTouchImageLinkAsync.pending, (state) => {
			state.isTrueTouchImageUploading = true;
		});
		builder.addCase(uploadStudioTrueTouchImageLinkAsync.fulfilled, (state, action) => {
			state.isTrueTouchImageUploading = false;
			state.trueTouchImage = action.payload;
		});
		builder.addCase(uploadStudioTrueTouchImageLinkAsync.rejected, (state) => {
			state.isTrueTouchImageUploading = false;
			sendToast(ToastType.UPLOAD_IMAGE_ERROR);
		});

		// delete multiple media
		builder.addCase(multiDeleteImageAsync.pending, (state) => {
			state.deletePromptAPIStage = ApiStage.LOADING;
		});
		builder.addCase(multiDeleteImageAsync.fulfilled, (state, action) => {
			const { mediaIds } = action.payload;
			const studioFeedChronologicalBatchIdListCopy = [...state.studioFeedChronologicalBatchIdList];
			const studioFeedBatchWiseMapCopy = { ...state.studioFeedBatchWiseMap };

			const deletedBatchIds = new Set();
			studioFeedChronologicalBatchIdListCopy.forEach((batchId) => {
				if (studioFeedBatchWiseMapCopy[batchId]) {
					const derivedMedia = {
						...studioFeedBatchWiseMapCopy[batchId].derivedMedia,
					};
					const remainingMediaIds = Object.keys(derivedMedia).filter(
						(mediaId) => !mediaIds.includes(mediaId),
					);

					if (remainingMediaIds.length > 0) {
						studioFeedBatchWiseMapCopy[batchId].derivedMedia = remainingMediaIds.reduce(
							(acc, mediaId) => {
								acc[mediaId] = derivedMedia[mediaId];
								return acc;
							},
							{},
						);
					} else {
						deletedBatchIds.add(batchId);
						delete studioFeedBatchWiseMapCopy[batchId];
					}
				}
			});
			state.studioFeedChronologicalBatchIdList = studioFeedChronologicalBatchIdListCopy.filter(
				(id) => !deletedBatchIds.has(id),
			);
			state.studioFeedBatchWiseMap = studioFeedBatchWiseMapCopy;
			state.studioMediaList = state.studioMediaList.filter(
				(mediaId) => !mediaIds.includes(mediaId),
			);
			const studioMediaMapCopy = { ...state.studioMediaMap };
			mediaIds.forEach((mediaId) => {
				delete studioMediaMapCopy[mediaId];
			});
			state.studioMediaMap = studioMediaMapCopy;
			deletedBatchIds.clear();
			state.deletePromptAPIStage = ApiStage.SUCCESS;
			state.openMultipleDeletePromptConfirmationPopUp = { open: false };
			sendToast(
				ToastType.CUSTOM,
				state.selectedImages && state.selectedImages.length > 1
					? 'Generations deleted successfully'
					: 'Generation deleted successfully',
			);
			state.selectedImages = [];
		});

		builder.addCase(multiDeleteImageAsync.rejected, (state, action) => {
			state.deletePromptAPIStage = ApiStage.ERROR;
			const { error } = action;
			handleSomethingWentWrongErrorWithToast('API request failed', error);
		});

		// randomise prompt
		builder.addCase(randomiseStudioPromptAsync.pending, (state) => {
			state.randomisePromptAPIStage = ApiStage.LOADING;
		});
		builder.addCase(randomiseStudioPromptAsync.fulfilled, (state, action) => {
			const prompt = action.payload;

			state.promptInput = prompt?.prompt ?? promptDefaultInput;
			state.negativePromptInput = prompt?.negativePrompt ?? negativePromptDefaultInput;

			state.selectedModels = [prompt?.model] ?? aspectRatioDefaultInput;

			state.aspectRatioInput = prompt?.aspectRatioType ?? aspectRatioDefaultInput;
			state.samplingStepsInput = prompt?.steps ?? samplingStepsDefaultInput;
			state.samplerInput = prompt?.sampler ?? samplerDefaultInput;
			state.AIGuidanceInput = prompt?.guidance ?? APGuidanceDefaultInput;

			state.seedInput = '-1';

			state.randomisePromptAPIStage = ApiStage.SUCCESS;
		});
		builder.addCase(randomiseStudioPromptAsync.rejected, (state, action) => {
			state.randomisePromptAPIStage = ApiStage.ERROR;
			const { error } = action;
			handleSomethingWentWrongErrorWithToast('API request failed', error);
		});

		// get style categories
		builder.addCase(getStylesCategoryAsync.pending, (state) => {
			state.getStyleCategoriesApiStage = ApiStage.LOADING;
		});

		builder.addCase(getStylesCategoryAsync.fulfilled, (state, action) => {
			const { data } = action.payload;
			state.getStyleCategoriesApiStage = ApiStage.SUCCESS;
			state.styleCategories = data;
		});

		builder.addCase(getStylesCategoryAsync.rejected, (state, action) => {
			state.getStyleCategoriesApiStage = ApiStage.ERROR;
			const { error } = action;
			handleSomethingWentWrongErrorWithToast('API request failed', error);
		});

		// get styles
		builder.addCase(getStylesAsync.pending, (state) => {
			state.getStylesApiStage = ApiStage.LOADING;
		});
		builder.addCase(getStylesAsync.fulfilled, (state, action) => {
			const { data, args } = action.payload;
			state.getStylesApiStage = ApiStage.SUCCESS;
			if (args?.type === GenerationStyleTypes.FLUX) {
				state.fluxStyles = data;
			} else {
				state.styles = data;
			}
		});
		builder.addCase(getStylesAsync.rejected, (state, action) => {
			state.getStylesApiStage = ApiStage.ERROR;
			const { error } = action;
			handleSomethingWentWrongErrorWithToast('API request failed', error);
		});

		// get control-net models
		builder.addCase(getControlNetModelsAsync.pending, (state) => {
			state.studioControlNetModelsApiStage = ApiStage.LOADING;
		});
		builder.addCase(getControlNetModelsAsync.fulfilled, (state, action) => {
			const { data } = action.payload;
			state.controlNetModelsStudio = data;
			state.studioControlNetModelsApiStage = ApiStage.SUCCESS;
		});
		builder.addCase(getControlNetModelsAsync.rejected, (state, action) => {
			state.studioControlNetModelsApiStage = ApiStage.ERROR;
			const { error } = action;
			handleSomethingWentWrongErrorWithToast('API request failed', error);
		});

		// submit media feedback
		builder.addCase(submitStudioMediaFeedbackAsync.pending, (state) => {
			state.submitStudioMediaFeedbackApiStage = ApiStage.LOADING;
		});
		builder.addCase(submitStudioMediaFeedbackAsync.fulfilled, (state) => {
			state.submitStudioMediaFeedbackApiStage = ApiStage.SUCCESS;
		});
		builder.addCase(submitStudioMediaFeedbackAsync.rejected, (state) => {
			state.submitStudioMediaFeedbackApiStage = ApiStage.ERROR;
		});

		// remove image background
		builder.addCase(productPhotoStudioRemoveImageBackgroundAsync.pending, (state) => {
			state.productPhotoWithoutBackgroundImageApiStage = ApiStage.LOADING;
		});
		builder.addCase(productPhotoStudioRemoveImageBackgroundAsync.fulfilled, (state, action) => {
			const { data } = action.payload;
			state.productPhotoWithoutBackgroundImage = { id: null, url: data?.output_image };
			state.productPhotoWithoutBackgroundImageApiStage = ApiStage.SUCCESS;
		});
		builder.addCase(productPhotoStudioRemoveImageBackgroundAsync.rejected, (state) => {
			state.productPhotoWithoutBackgroundImageApiStage = ApiStage.ERROR;
			sendToast(ToastType.CUSTOM, 'Failed to remove background');
		});

		// Face replace cost
		builder.addCase(getStudioFaceReplaceCostAsync.pending, (state) => {
			state.faceReplaceCostAPIStage = ApiStage.LOADING;
		});
		builder.addCase(getStudioFaceReplaceCostAsync.fulfilled, (state, action) => {
			const { data } = action.payload;
			state.faceReplaceCostAPIStage = ApiStage.SUCCESS;
			state.faceReplaceCost = data;
		});
		builder.addCase(getStudioFaceReplaceCostAsync.rejected, (state, action) => {
			state.faceReplaceCostAPIStage = ApiStage.ERROR;
			const { error } = action;
			handleSomethingWentWrongErrorWithToast('API request failed', error);
		});

		// audio asset templates
		builder.addCase(getAudioAssetsTemplatesAsync.pending, (state) => {
			state.musicVideoTemplatesApiStage = ApiStage.LOADING;
		});
		builder.addCase(getAudioAssetsTemplatesAsync.fulfilled, (state, action) => {
			const { data } = action.payload;
			state.musicVideoTemplatesApiStage = ApiStage.SUCCESS;
			state.musicVideoTemplatesData = data.map(formatTemplateData);
		});
		builder.addCase(getAudioAssetsTemplatesAsync.rejected, (state, action) => {
			state.musicVideoTemplatesApiStage = ApiStage.ERROR;
			const { error } = action;
			handleSomethingWentWrongErrorWithToast('API request failed', error);
		});

		builder.addCase(getMusicVideoAsync.fulfilled, (state, action) => {
			const { route } = action.meta.arg;
			const { data } = action.payload;
			const { generation_id: batchId, id: derivedMediaId, state: generationState } = data;
			if (route === TEXT_TO_IMAGE && batchId) {
				const studioFeedBatchWiseMapCopy = { ...state.studioFeedBatchWiseMap };
				studioFeedBatchWiseMapCopy[batchId] = {
					...studioFeedBatchWiseMapCopy[batchId],
					derivedMedia: {
						...studioFeedBatchWiseMapCopy[batchId].derivedMedia,
						[derivedMediaId]: {
							...studioFeedBatchWiseMapCopy[batchId].derivedMedia[derivedMediaId],
							musicVideoStates: generationState,
						},
					},
				};
				state.studioFeedBatchWiseMap = { ...studioFeedBatchWiseMapCopy };
			}
		});
		builder.addCase(getMusicVideoAsync.rejected, (action) => {
			const { error } = action;
			handleSomethingWentWrongErrorWithToast('API request failed', error);
		});
	},
});

export const {
	setRandomiseStudioPrompt,
	setStudioSidebarView,
	setStudioGenerationSidebarView,
	setStudioAppBarMediaType,
	setFeedView,
	setFeedType,

	setStudioSettings,

	setStudioSelectedModels,
	setStudioSelectedStyles,
	setStudioSelectedFluxModels,
	setStudioSelectedLightningModels,
	setStudioPromptInput,
	setStudioNegativePromptInput,
	setStudioSamplerInput,
	setStudioSamplingStepsInput,
	setStudioAIGuidanceInput,
	setStudioBatchSizeInput,
	setStudioSeedInput,
	setStudioRandomiseSeedInput,
	setStudioAspectRatioInput,

	setStudioQuality,

	setCharacterPromptInputs,
	setRandomizeCharacterPrompt,

	setOutputStudioMedia,

	setStudioCurrentGeneration,
	setStudioCurrentChangeItGeneration,
	setStudioCurrentTrueTouchGeneration,
	setStudioCurrentUpscaleGeneration,
	setStudioCurrentVideoFaceSwapGeneration,
	setStudioCurrentVideoAnyoneGeneration,
	setStudioFaceReplaceGeneration,
	setStudioMusicVideoGeneration,
	setStudioClothingChangeGeneration,
	resetStudioCurrentGenerations,
	toggleTooManyGenerationsModal,

	setStudioFeed,
	setGetStudioFeedAPIStage,
	setStudioFeedCurrentPage,

	setStudioControlNetEnabled,
	setStudioControlNetImage,
	resetStudioControlNetImage,
	setStudioControlNetNonSDXLType,
	setStudioControlNetSDXLType,
	setStudioControlNetFluxType,
	setStudioControlNetControlMode,
	setStudioControlNetResizeMode,

	setStudioFaceSwapImageAssetData,
	resetStudioFaceSwapImageAssetData,
	setStudioFaceSwapImageAdded,

	setStudioFaceSwapVideoAssetData,
	resetStudioFaceSwapVideoAssetData,

	setStudioFaceSwapRenderingPreference,

	setStudioCharacterLabEnabled,
	setStudioFacelockEnabled,
	setStudioFacelockImage,
	resetStudioFacelockImage,
	resetStudioChangeAnythingImage,

	setWhatToChange,
	setChangeTo,

	setStudioTrueTouchRealismEffect,
	setStudioTrueTouchScaleFactor,
	resetStudioTrueTouchImage,

	setStudioUpscaleImage,
	resetStudioUpscaleImage,
	setStudioUpscaleOutputResolution,

	setStudioVideoAnyoneImage,
	setStudioVideoAnyoneImageId,
	resetStudioVideoAnyoneImage,
	setStudioVideoAnyonePrompt,
	setStudioVideoAnyoneDuration,

	setHomePagePromptInput,
	setStudioLipSyncVideoAssetData,
	resetStudioLipSyncVideoAssetData,
	setStudioLipSyncImageAssetData,
	resetStudioLipSyncImageAssetData,
	setStudioLipSyncAudioAssetData,
	resetStudioLipSyncAudioAssetData,
	setStudioLipSyncScript,
	setStudioLipSyncVoiceData,
	toggleMultiDeletePromptConfirmationModal,
	setStudioSelectedImages,
	updateStudioSelectedImages,
	setStudioIsLipSyncFreeVideoUsed,

	toggleIsStudioSidebarOpen,
	toggleStudioMediaFeedbackModal,
	togglePromptDetailModalOpen,
	setStudioSelectedFluxStyles,
	setStudioFaceReplaceImage,
	setStudioFaceReplaceImageId,
	resetStudioFaceReplaceImage,
	setStudioFaceReplaceTargetImageIndex,
	setStudioFaceReplaceTargetImages,
	removeStudioFaceReplaceTargetImage,
	resetStudioFaceReplaceTargetImages,
	toggleStudioAudioCroppingModalOpen,
	setStudioMusicVideoAudioAssetData,
	resetStudioMusicVideoAudioAssetData,
	setStudioMusicVideoTheme,
	setStudioMusicVideoCharacter,
	setStudioMusicVideoAspectRatio,
	setStudioClothingInputImage,
	setStudioClothingTargetImage,
	setStudioClothingTypeValue,
	resetStudioClothingInputImage,
	resetStudioClothingTargetImage,
	toggleShowGuide,
	resetStudioState,
	toggleStudioContextMenu,
	setStudioProductPhotoImage,
	setStudioProductPhotoWithoutBackgroundImage,
	resetStudioProductPhotoImage,
	setStudioProductPhotoBackgroundImage,
	setStudioProductPhotoObjectLocationImage,
	setStudioProductPhotoCompositeImage,
	resetStudioProductPhotoBackgroundImage,
	setStudioProductPhotoMode,
	setStudioProductPhotoReflection,
	resetStudioProductPhotoInternalAssets,
	setStudioProductPhotoGeneration,
	setStudioProductPhotoObjectTransformValues,
	setStudioFluxModels,
	setStudioFluxStyles,
	setStudioModels,
	setStudioStyles,

	setStudioImageReferenceStyle,
	resetStudioImageReferenceStyle,
	setStudioIntensityCountInput,
	toggleStudioProductPhotoImageToVideoGuide,

	setStudioVideoAnyoneEndFrameImage,
	resetStudioVideoAnyoneEndFrameImage,
	setStudioVideoAnyoneModelName,
	setStudioVideoAnyoneModelProvider,
} = studioSlice.actions;

export const randomiseStudioPromptSelector = (state) => state.studio.randomisePrompt;

export const studioSidebarViewSelector = (state) => state.studio.sidebarView;
export const studioGenerationSidebarViewSelector = (state) => state.studio.sidebarGenerationTab;
export const studioMediaTypeSelector = (state) => state.studio.studioAppBarMediaType;
export const studioFeedViewSelector = (state) => state.studio.feedView;
export const studioFeedTypeSelector = (state) => state.studio.feedType;
export const modelsSelector = (state) => state.studio.models;
export const getModelsAPIStageSelector = (state) => state.studio.getModelsAPIStage;
export const studioSelectedModelsSelector = (state) => state.studio.selectedModels;

export const studioFluxModelsSelector = (state) => state.studio.fluxModels;
export const studioSelectedFluxModelsSelector = (state) => state.studio.selectedFluxModels;
export const studioLightningModelsSelector = (state) => state.studio.lightningModels;
export const studioSelectedLightningModelsSelector = (state) =>
	state.studio.selectedLightningModels;

export const modelCategoriesSelector = (state) => state.studio.modelCategories;
export const modelCategoriesAPIStageSelector = (state) => state.studio.getModelCategoriesAPIStage;

export const lorasSelector = (state) => state.studio.loras;
export const getLorasApiStageSelector = (state) => state.studio.getLorasAPIStage;
export const selectedLorasSelector = (state) => state.studio.selectedLoras;

export const studioControlNetModelsApiStageSelector = (state) =>
	state.studio.studioControlNetModelsApiStage;

export const getStudioFeedAPIStageSelector = (state) => state.studio.getStudioFeedAPIStage;

export const studioFeedBatchWiseMapSelector = (state) => state.studio.studioFeedBatchWiseMap;
export const studioFeedChronologicalBatchIdListSelector = (state) =>
	state.studio.studioFeedChronologicalBatchIdList;
export const studioMediaListSelector = (state) => state.studio.studioMediaList;
export const studioMediaMapSelector = (state) => state.studio.studioMediaMap;
export const studioMediaItemSelector = createSelector(
	[studioMediaMapSelector, studioFeedBatchWiseMapSelector, (state, mediaId) => mediaId],
	(studioMediaMap, studioFeedBatchWiseMap, mediaId) => {
		const { batchId } = studioMediaMap[mediaId] ?? {};
		const { generationType } = studioFeedBatchWiseMap[batchId] ?? {};
		return { ...studioFeedBatchWiseMap[batchId]?.derivedMedia[mediaId], generationType };
	},
);

export const studioFeedTotalPagesSelector = (state) => state.studio.studioFeedTotalPages;
export const studioFeedCurrentPageSelector = (state) => state.studio.studioFeedCurrentPage;

export const studioPromptInputSelector = (state) => state.studio.promptInput;
export const homePagePromptInputSelector = (state) => state.studio.homePagePromptInput;

export const characterPromptInputSelector = (state) => state.studio.defaultSelected;

export const studioNegativePromptInputSelector = (state) => state.studio.negativePromptInput;
export const studioSamplerInputSelector = (state) => state.studio.samplerInput;
export const studioSamplingStepsInputSelector = (state) => state.studio.samplingStepsInput;
export const studioAIGuidanceInputSelector = (state) => state.studio.AIGuidanceInput;
export const studioBatchSizeInputSelector = (state) => state.studio.batchSizeInput;
export const studioSeedInputSelector = (state) => state.studio.seedInput;
export const studioRandomiseSeedInputSelector = (state) => state.studio.randomiseSeedInput;
export const studioAspectRatioInputSelector = (state) => state.studio.aspectRatioInput;

export const studioQualitySelector = (state) => state.studio.quality;

export const studioCurrentGenerationSelector = (state) => state.studio.currentGeneration;
export const studioCurrentChangeItGenerationSelector = (state) =>
	state.studio.currentChangeItGeneration;
export const studioTrueTouchGenerationSelector = (state) => state.studio.currentTrueTouchGeneration;
export const studioProductPhotoGenerationSelector = (state) =>
	state.studio.currentProductPhotoGeneration;
export const studioFaceReplaceGenerationSelector = (state) =>
	state.studio.currentFaceReplaceGeneration;
export const studioMusicVideoGenerationSelector = (state) =>
	state.studio.currentMusicVideoGeneration;
export const studioClothingChangeGenerationSelector = (state) =>
	state.studio.currentClothingChangeGeneration;

export const generateStudioMediaAPIStageSelector = (state) =>
	state.studio.generateStudioMediaAPIStage;
export const outputStudioMediaSelector = (state) => state.studio.outputStudioMedia;

export const openTooManyGenerationsModalSelector = (state) =>
	state.studio.openTooManyGenerationsModal;

export const studioControlNetEnabledSelector = (state) => state.studio.controlNetEnabled;
export const studioControlNetNonSDXLTypeSelector = (state) => state.studio.controlNetNonSDXLType;
export const studioControlNetSDXLTypeSelector = (state) => state.studio.controlNetSDXLType;
export const studioControlNetFluxTypeSelector = (state) => state.studio.controlNetFluxType;

export const studioControlNetImageSelector = (state) => state.studio.controlNetImage;
export const studioControlNetControlModeSelector = (state) => state.studio.controlNetControlMode;
export const studioControlNetResizeModeSelector = (state) => state.studio.controlNetResizeMode;
export const studioControlNetModelsSelector = (state) => state.studio.controlNetModelsStudio;

export const studioCharacterLabEnabledSelector = (state) => state.studio.characterLabEnabled;
export const studioFacelockEnabledSelector = (state) => state.studio.facelockEnabled;
export const studioFacelockImageSelector = (state) => state.studio.facelockImage;
export const studioChangeAnythingImageSelector = (state) => state.studio.changeAnythingImage;

export const whatToChangeSelector = (state) => state.studio.whatToChange;
export const changeToSelector = (state) => state.studio.changeTo;

export const studioTrueTouchImageSelector = (state) => state.studio.trueTouchImage;

export const studioTrueTouchScaleFactorSelector = (state) => state.studio.trueTouchScaleFactor;
export const studioTrueTouchRealismEffectSelector = (state) => state.studio.trueTouchRealismEffect;
export const studioIsTrueTouchImageUploadingSelector = (state) =>
	state.studio.isTrueTouchImageUploading;

// 8k Upscale Selectors
export const studioUpscaleImageSelector = (state) => state.studio.upscaleInputImage;
export const studioUpscaleOutputResolutionSelector = (state) =>
	state.studio.upscaleOutputResolution;

// video anyone selectors
export const studioVideoAnyoneImageSelector = (state) => state.studio.videoAnyoneInputImage;
export const studioVideoAnyonePromptSelector = (state) => state.studio.videoAnyonePrompt;
export const studioVideoAnyoneDurationSelector = (state) => state.studio.videoAnyoneDuration;

// video face swap selectors
export const studioVideoFaceSwapImageSelector = (state) => state.studio.faceSwapImageAssetData;
export const studioVideoFaceSwapVideoSelector = (state) => state.studio.faceSwapVideoAssetData;
export const studioVideoFaceSwapRendingPrefSelector = (state) =>
	state.studio.faceSwapRenderingPreference;
export const faceSwapImageAddedSelector = (state) => state.studio.faceSwapStudioImageAdded;

// face replace selectors
export const studioFaceReplaceImageSelector = (state) => state.studio.faceReplaceInputImage;
export const studioFaceReplaceTargetImagesSelector = (state) =>
	state.studio.faceReplaceTargetImages;
export const studioFaceReplaceTargetImageIndexSelector = (state) =>
	state.studio.faceReplaceTargetImageIndex;
export const studioFaceReplaceCostSelector = (state) => state.studio.faceReplaceCost;
export const studioFaceReplaceCostAPIStageSelector = (state) =>
	state.studio.faceReplaceCostAPIStage;

export const openDeletePromptConfirmationModalSelector = (state) =>
	state.studio.openDeletePromptConfirmationPopUp;
export const openMultipleDeletePromptConfirmationModalSelector = (state) =>
	state.studio.openMultipleDeletePromptConfirmationPopUp;
export const deletePromptDetailsSelector = (state) => state.studio.deletePromptDetails;
export const multiDeletePromptDetailsSelector = (state) => state.studio.multiDeletePromptDetails;
export const deletePromptAPIStageSelector = (state) => state.studio.deletePromptAPIStage;

export const randomiseStudioPromptAPIStageSelector = (state) =>
	state.studio.randomisePromptAPIStage;

export const studioStyleCategoriesApiStageSelector = (state) =>
	state.studio.getStyleCategoriesApiStage;
export const studioStyleCategoriesSelector = (state) => state.studio.styleCategories;
export const studioStylesApiStageSelector = (state) => state.studio.getStylesApiStage;
export const studioStylesSelector = (state) => state.studio.styles;
export const studioFluxStylesSelector = (state) => state.studio.fluxStyles;
export const studioSelectedFluxStylesSelector = (state) => state.studio.selectedFluxStyles;
export const studioSelectedStylesSelector = (state) => state.studio.selectedStyles;
export const lipSyncVideoAssetDataSelector = (state) => state.studio.lipSyncVideoAssetData;
export const lipSyncImageAssetDataSelector = (state) => state.studio.lipSyncImageAssetData;
export const lipSyncAudioAssetDataSelector = (state) => state.studio.lipSyncAudioAssetData;
export const lipSyncScriptSelector = (state) => state.studio.lipSyncScript;
export const lipSyncVoiceDataSelector = (state) => state.studio.lipSyncVoiceData;
export const lipSyncIsFreeVideoUsedSelector = (state) => state.studio.lipSyncIsFreeVideoUsed;

export const studioSelectedImagesSelector = (state) => state.studio.selectedImages;

export const isStudioSidebarOpenSelector = (state) => state.studio.isStudioSidebarOpen;
export const selectedStudioGenerationStyleSelector = createSelector(
	[
		(state) => state.studio.sidebarGenerationTab,
		(state) => state.studio.selectedStyles,
		(state) => state.studio.selectedModels,
		(state) => state.studio.selectedFluxModels,
		(state) => state.studio.selectedFluxStyles,
		(state) => state.studio.selectedLightningModels,
		(state) => state.studio.imageReferenceStyle,
	],
	(
		sidebarGenerationTab,
		selectedStyles,
		selectedModels,
		selectedFluxModels,
		selectedFluxStyles,
		selectedLightningModels,
		selectedImageReferenceStyle,
	) => {
		if (selectedImageReferenceStyle?.url !== null) {
			return [selectedImageReferenceStyle];
		}

		switch (sidebarGenerationTab) {
			case StudioSidebarViews.MAGICMODE:
				return selectedStyles;
			case StudioSidebarViews.PROMODE:
				return selectedModels;
			case StudioSidebarViews.FLUX:
				return [...selectedFluxModels, ...selectedFluxStyles];
			case StudioSidebarViews.LIGHTNING:
				return selectedLightningModels;
			default:
				return [];
		}
	},
);

export const studioMediaFeedbackModalSelector = (state) =>
	state.studio.studioMediaFeedbackModalOpen;
export const promptDetailModalOpenSelector = (state) => state.studio.promptDetailModalOpen;

export const studioMusicVideoAudioAssetDataSelector = (state) =>
	state.studio.musicVideoAudioAssetData;
export const studioMusicVideoThemeSelector = (state) => state.studio.musicVideoTheme;
export const studioMusicVideoCharacterSelector = (state) => state.studio.musicVideoCharacter;
export const studioMusicVideoAspectRatioSelector = (state) => state.studio.musicVideoAspectRatio;
export const studioMusicVideoTemplatesDataSelector = (state) =>
	state.studio.musicVideoTemplatesData;
export const studioMusicVideoTemplatesApiStageSelector = (state) =>
	state.studio.musicVideoTemplatesApiStage;
export const studioClothingInputImageSelector = (state) => state.studio.clothingInputImage;
export const studioClothingTargetImageSelector = (state) => state.studio.clothingTargetImage;
export const studioClothingTypeValueSelector = (state) => state.studio.clothingTypeValue;
export const studioContextMenuSelector = (state) => state.studio.studioContextMenu;
export const studioShowGuideSelector = (state) => state.studio.showGuide;

// Product Photo selectors
export const studioProductPhotoImageSelector = (state) => state.studio.productPhotoImage;
export const studioProductPhotoWithoutBackgroundImageApiStageSelector = (state) =>
	state.studio.productPhotoWithoutBackgroundImageApiStage;
export const studioProductPhotoWithoutBackgroundImageSelector = (state) =>
	state.studio.productPhotoWithoutBackgroundImage;
export const studioProductPhotoBackgroundImageSelector = (state) =>
	state.studio.productPhotoBackgroundImage;
export const studioProductPhotoObjectLocationImageSelector = (state) =>
	state.studio.productPhotoObjectLocationImage;
export const studioProductPhotoCompositeImageSelector = (state) =>
	state.studio.productPhotoCompositeImage;
export const studioProductPhotoModeSelector = (state) => state.studio.productPhotoMode;
export const studioProductPhotoReflectionSelector = (state) => state.studio.productPhotoReflection;
export const studioProductPhotoImageToVideoGuideToggleSelector = (state) =>
	state.studio.productPhotoImageToVideoGuideToggle;

// Image Reference selector
export const studioImageReferenceStyleSelector = (state) => state.studio.imageReferenceStyle;
export const studioIntensityCountInputSelector = (state) => state.studio.intensityCountInput;

export const studioVideoAnyoneEndFrameImageSelector = (state) =>
	state.studio.videoAnyoneEndFrameImage;
export const studioVideoAnyoneModelNameSelector = (state) => state.studio.videoAnyoneModelName;

export const handleStudioProductPhotoAssetFromPusher = (newAsset) => async (dispatch, getState) => {
	const productPhotoObjectLocationImage = studioProductPhotoObjectLocationImageSelector(getState());
	const productPhotoCompositeImage = studioProductPhotoCompositeImageSelector(getState());

	if (newAsset.status === 'active' && newAsset.id === productPhotoObjectLocationImage.id) {
		dispatch(
			setStudioProductPhotoObjectLocationImage({
				...productPhotoObjectLocationImage,
				status: 'success',
			}),
		);
	}
	if (newAsset.status === 'active' && newAsset.id === productPhotoCompositeImage.id) {
		dispatch(
			setStudioProductPhotoCompositeImage({
				...productPhotoCompositeImage,
				status: 'success',
			}),
		);
	}
};

export default studioSlice.reducer;
